import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { trackPageView } from '../../services/analytics';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  className?: string;
  screenName?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  className = '',
  screenName,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    if (screenName) {
      trackPageView(`/modal/${screenName}`, title);
    }

    // モーダルが開いているときはスクロールを無効化
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // フォーカスをモーダルに移動
    if (modalRef.current) {
      modalRef.current.focus();
    }

    // キーボードイベントリスナーを追加
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // モーダルが閉じられたらスクロールを復元
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, screenName, title]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleOverlayClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleOverlayClick(e as any);
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="モーダルを閉じる"
      data-testid="modal-overlay"
    >
      <div
        ref={modalRef}
        className={`bg-gray-800 text-white rounded-xl p-5 max-w-[90vw] max-h-[80vh] overflow-y-auto shadow-2xl ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
      >
        <div id="modal-title" className="mb-3 font-bold text-green-600 text-lg">
          {title}
        </div>
        <div className="mb-4 leading-relaxed">{children}</div>
        <div className="text-center">
          <button
            onClick={onClose}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition-colors"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;
